import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Chip, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, CircularProgress, Snackbar, IconButton,
  Alert, Switch, FormControlLabel,
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { useState, useEffect } from 'react';
import api from '../../api/axios';

const fmt = (n) => new Intl.NumberFormat('en-PK').format(+n || 0);

const EMPTY = { name: '', slug: '', price_monthly: '', price_yearly: '', max_users: 5, max_products: 500, max_branches: 1, is_active: true };

function PlanDialog({ plan, open, onClose, onSuccess }) {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const isEdit = !!plan?.id;

  useEffect(() => {
    setForm(plan ? { ...plan, is_active: !!plan.is_active } : EMPTY);
    setError('');
  }, [plan, open]);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handle = async () => {
    if (!form.name || !form.slug) { setError('Name and slug are required'); return; }
    setLoading(true); setError('');
    try {
      if (isEdit) await api.put(`/superadmin/plans/${plan.id}`, form);
      else await api.post('/superadmin/plans', form);
      onSuccess();
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to save plan');
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Edit Plan' : 'New Subscription Plan'}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        {error && <Alert severity="error">{error}</Alert>}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField label="Plan Name" value={form.name} onChange={set('name')} fullWidth required />
          <TextField label="Slug (e.g. starter)" value={form.slug} onChange={set('slug')} fullWidth required disabled={isEdit} />
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField label="Monthly Price (₨)" type="number" value={form.price_monthly} onChange={set('price_monthly')} fullWidth inputProps={{ min: 0 }} />
          <TextField label="Yearly Price (₨)" type="number" value={form.price_yearly} onChange={set('price_yearly')} fullWidth inputProps={{ min: 0 }} />
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField label="Max Users (-1 = unlimited)" type="number" value={form.max_users} onChange={set('max_users')} fullWidth />
          <TextField label="Max Products (-1 = unlimited)" type="number" value={form.max_products} onChange={set('max_products')} fullWidth />
          <TextField label="Max Branches" type="number" value={form.max_branches} onChange={set('max_branches')} fullWidth inputProps={{ min: 1 }} />
        </Box>
        <FormControlLabel
          control={<Switch checked={!!form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} />}
          label="Plan is active (visible to customers)"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handle} disabled={loading}>
          {loading ? <CircularProgress size={20} /> : isEdit ? 'Save Changes' : 'Create Plan'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function PlansPage() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [snack, setSnack] = useState('');

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get('/superadmin/plans'); setPlans(data.data || []); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this plan?')) return;
    try {
      await api.delete(`/superadmin/plans/${id}`);
      setSnack('Plan deleted'); load();
    } catch (e) { setSnack(e.response?.data?.message || 'Delete failed'); }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>Subscription Plans</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => { setSelected(null); setDialogOpen(true); }}>New Plan</Button>
      </Box>

      <Card>
        <CardContent>
          {loading ? <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box> : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Plan</TableCell>
                  <TableCell>Slug</TableCell>
                  <TableCell align="right">Monthly</TableCell>
                  <TableCell align="right">Yearly</TableCell>
                  <TableCell align="center">Users</TableCell>
                  <TableCell align="center">Products</TableCell>
                  <TableCell align="center">Branches</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {plans.map(p => (
                  <TableRow key={p.id} hover>
                    <TableCell><Typography fontWeight={700}>{p.name}</Typography></TableCell>
                    <TableCell><Chip label={p.slug} size="small" variant="outlined" /></TableCell>
                    <TableCell align="right">₨ {fmt(p.price_monthly)}</TableCell>
                    <TableCell align="right">₨ {fmt(p.price_yearly)}</TableCell>
                    <TableCell align="center">{p.max_users === -1 ? '∞' : p.max_users}</TableCell>
                    <TableCell align="center">{p.max_products === -1 ? '∞' : p.max_products}</TableCell>
                    <TableCell align="center">{p.max_branches === -1 ? '∞' : p.max_branches}</TableCell>
                    <TableCell align="center">
                      <Chip label={p.is_active ? 'Active' : 'Hidden'} size="small" color={p.is_active ? 'success' : 'default'} />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton size="small" onClick={() => { setSelected(p); setDialogOpen(true); }}><Edit fontSize="small" /></IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(p.id)}><Delete fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {plans.length === 0 && (
                  <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>No plans yet</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <PlanDialog
        plan={selected} open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSuccess={() => { setDialogOpen(false); setSnack(selected ? 'Plan updated' : 'Plan created'); load(); }}
      />
      <Snackbar open={!!snack} autoHideDuration={4000} onClose={() => setSnack('')} message={snack} />
    </Box>
  );
}
